import Image from "next/image";
import Link from "next/link";
import React, { useEffect, useMemo, useState } from "react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";

import NewsletterForm from "@/components/NewsletterForm";
import { isBlackWeek, SHOW_NEWSLETTER_BANNER } from "./../../config";

/**
 * `sticky`: true klebt der Kopf oben, false nie — und "lg" erst ab Desktop.
 *
 * "lg" ist für Seiten, die im Wesentlichen eine lange Liste sind (Absprache
 * 2026-10-07): Auf dem Handy nahm der klebende Kopf 56 von 812 Punkten weg,
 * und darunter klebte noch die Filterleiste. Am Schreibtisch ist der Platz
 * da, und der Kopf ist die einzige Navigation.
 */
const Header = ({ sticky = true }) => {
  const { t } = useTranslation("common");
  const { pathname, locale } = useRouter();

  const showBlackWeekBanner = isBlackWeek();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Newsletter Banner logic
  const excludedPaths = useMemo(
    () => ["/polaroid-kamera-mieten", "/sofortbildkamera-mieten"],
    [],
  );
  const isExcluded = excludedPaths.includes(pathname);

  const [showNewsletterBanner, setShowNewsletterBanner] = useState(() => {
    if (typeof window === "undefined") return !isExcluded && locale === "de";
    const isNewsletterFilledOut = localStorage.getItem("NewsletterFilledOut");
    return !isExcluded && !isNewsletterFilledOut && locale === "de";
  });

  useEffect(() => {
    const isNewsletterFilledOut = localStorage.getItem("NewsletterFilledOut");
    if (isExcluded || isNewsletterFilledOut || locale !== "de") {
      setShowNewsletterBanner(false);
    }
  }, [pathname, locale, isExcluded]);

  useEffect(() => {
    // close mobile menu on route change
    setMobileOpen(false);
  }, [pathname]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleNewsletterSubmit = () => {
    localStorage.setItem("NewsletterFilledOut", "true");
    setShowNewsletterBanner(false);
    setIsModalOpen(false);
  };

  const handleNavClick = (event, href) => {
    if (typeof href !== "string") return;
    if (href.startsWith("/#") && pathname === "/") {
      event.preventDefault();
      const id = href.slice(2);
      const target = document.getElementById(id);
      if (target) {
        const offset = Number(target.dataset.scrollOffset || 0);
        const top =
          target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }
    setMobileOpen(false);
  };

  // ✅ Neue URL-Struktur: passe das hier zentral an
  const nav = useMemo(() => {
    // DE (FotoboxGuru) / EN (BoothScanner) könnte später abweichen
    const items = [
      { label: "Fotobox-Finder", href: "/fotobox-finder", rel: "nofollow" },
      { label: "Polaroid Kamera mieten", href: "/polaroid-kamera-mieten" },
    ];

    return items;
  }, [locale]);

  return (
    <>
      {/* Sticky Apple-clean header */}
      <header
        className={`${
          sticky === "lg"
            ? "lg:sticky lg:top-0 z-50"
            : sticky
              ? "sticky top-0 z-50"
              : ""
        } border-b border-slate-200/70 bg-white/70 backdrop-blur`}
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 lg:px-8">
          {/* Logo */}
          <Link href="/" className="shrink-0">
            <Image
              src={
                locale === "de"
                  ? "/logo/fotoboxguru-logo.svg"
                  : "/logo/boothscanner-logo.svg"
              }
              width={220}
              height={56}
              alt={locale === "de" ? "FotoboxGuru Logo" : "BoothScanner Logo"}
              className="h-auto w-40 sm:w-44 lg:w-52"
              priority
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {nav.map((item) => {
              const active =
                item.href !== "/" && pathname.startsWith(item.href);
              return (
                <Link
                  key={item.href}
                  href={item.href}
                  rel={item.rel}
                  onClick={(event) => handleNavClick(event, item.href)}
                  className={[
                    "rounded-full px-4 py-2 text-sm font-semibold transition",
                    active
                      ? "bg-slate-900 text-white"
                      : "text-slate-700 hover:bg-slate-100 hover:text-slate-900",
                  ].join(" ")}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            {/* Primary CTA */}
            <Link
              href="/fotobox-vergleich"
              className="hidden rounded-full bg-[#FF5744] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#eb4836] hover:shadow-md sm:inline-flex"
            >
              Fotobox-Vergleich
            </Link>

            {/* Mobile menu button */}
            <button
              type="button"
              onClick={() => setMobileOpen((s) => !s)}
              className="inline-flex items-center justify-center rounded-full bg-white px-3 py-2 text-sm font-semibold text-slate-900 ring-1 ring-slate-200/80 shadow-sm transition hover:shadow-md md:hidden"
              aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? (
                <XMarkIcon className="h-5 w-5" />
              ) : (
                <Bars3Icon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200/70 bg-white">
            <div className="mx-auto max-w-7xl px-4 py-4">
              <div className="grid gap-2">
                {nav.map((item) => (
                  <Link
                    key={item.href}
                    href={item.href}
                    rel={item.rel}
                    onClick={(event) => handleNavClick(event, item.href)}
                    className="rounded-2xl bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-900 ring-1 ring-slate-200/70"
                  >
                    {item.label}
                  </Link>
                ))}

                <Link
                  href="/fotobox-finder"
                  className="mt-2 inline-flex items-center justify-center rounded-2xl bg-[#FF5744] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-[#eb4836] hover:shadow-md"
                >
                  Vergleich starten
                </Link>
              </div>
            </div>
          </div>
        )}
      </header>

      {/* Banners (cleaner style) */}
      {showBlackWeekBanner && (
        <div className="border-b border-slate-200/70 bg-slate-900 text-white">
          <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-3 text-center text-sm lg:flex-row lg:text-left lg:px-8">
            <div className="inline-flex items-center gap-2">
              <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
                BlackWeek
              </span>
              <span>
                <strong>50% Rabatt</strong> auf den Mietpreis einer
                Polaroid-Sofortbildkamera – Code <strong>BLACKWEEK50</strong>
              </span>
            </div>

            <Link
              href="/polaroid-kamera-mieten?ref=index-blackweek-banner"
              className="inline-flex items-center justify-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm transition hover:shadow-md"
            >
              Zu den Kameras
            </Link>
          </div>
        </div>
      )}

      {SHOW_NEWSLETTER_BANNER &&
        !showBlackWeekBanner &&
        showNewsletterBanner && (
          <div className="border-b border-slate-200/70 bg-slate-900 text-white">
            <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-3 text-center text-sm lg:flex-row lg:text-left lg:px-8">
              <div className="inline-flex items-center gap-2">
                <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-semibold">
                  Fotobox Gutscheine
                </span>
                <span>
                  Erhalte <strong>exklusive Fotobox Gutscheine</strong>{" "}
                  verschiedener Anbieter.
                </span>
              </div>

              <button
                type="button"
                onClick={openModal}
                className="inline-flex items-center justify-center rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-900 shadow-sm transition hover:shadow-md"
              >
                Jetzt anmelden
              </button>
            </div>
          </div>
        )}

      {/* Newsletter Modal */}
      {isModalOpen && (
        <NewsletterForm
          onClose={closeModal}
          onSubmit={handleNewsletterSubmit}
        />
      )}
    </>
  );
};

export default Header;
